import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { api } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { formatDateTime } from '../lib/utils';
import { formatDuration, KIND_KEY, PHASE_KEY, phaseTone, RAIL_KEY, railTone } from '../lib/contest';
import type { ContestResults } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { FullPageLoader } from '../components/ui/spinner';

/**
 * Published results.
 *
 * The API refuses this endpoint until the contest is published, and the payload
 * carries only a display name and a result per participant — no email, no id, no
 * payment data. The answer key appears here, and only here, because the contest
 * is over.
 */
export default function ContestResultsPage() {
  const { id = '' } = useParams();
  const { t, locale } = useTranslation();
  const [data, setData] = useState<ContestResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.get<ContestResults>(`/contests/${id}/results`));
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <FullPageLoader label={t('common.loading')} />;
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        {/* Results that are simply not published yet are an ordinary state, so the page
            keeps its heading and says so plainly instead of looking like a failure. */}
        <h1 className="text-2xl font-semibold tracking-tight text-fg">
          {t('contest.results.title')}
        </h1>
        <div className="mt-6">
          <Alert tone={error ? 'error' : 'info'}>
            {error ?? t('contest.results.notPublished')}
          </Alert>
        </div>
        <div className="mt-6">
          <Link to={`/contests/${id}`}>
            <Button variant="outline">{t('common.backToContest')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { contest, leaderboard, winners, questions } = data;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm">
        <Link to={`/contests/${id}`} className="text-muted hover:text-fg">
          {contest.title}
        </Link>
      </nav>

      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-fg">
            {t('contest.results.title')}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge tone={railTone(contest.rail)}>{t(RAIL_KEY[contest.rail])}</Badge>
            <Badge tone={phaseTone(contest.phase)}>{t(PHASE_KEY[contest.phase])}</Badge>
            <span className="text-sm text-muted">
              {contest.prizePool} {contest.asset}
            </span>
          </div>
        </div>

        <a href={`/api/contests/${id}/results.csv`} download>
          <Button variant="outline">{t('contest.results.export')}</Button>
        </a>
      </header>

      {winners.length > 0 && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>{t('contest.results.winners')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col gap-2">
              {winners.map((winner) => (
                <li
                  key={winner.rank}
                  className="flex items-center justify-between gap-4 text-sm"
                >
                  <span className="font-medium text-fg">
                    {winner.rank}. {winner.displayName ?? t('common.notAvailable')}
                  </span>
                  <span className="font-semibold text-fg">
                    {winner.prize} {contest.asset}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>{t('contest.results.leaderboard')}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {leaderboard.length === 0 ? (
            <p className="text-sm text-muted">{t('contest.results.empty')}</p>
          ) : (
            <table className="w-full text-sm">
              <caption className="sr-only">{t('contest.results.leaderboard')}</caption>
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted">
                  <th scope="col" className="px-2 py-2 text-start">
                    {t('contest.results.rank')}
                  </th>
                  <th scope="col" className="px-2 py-2 text-start">
                    {t('contest.results.participant')}
                  </th>
                  <th scope="col" className="px-2 py-2 text-start">
                    {t('contest.results.score')}
                  </th>
                  <th scope="col" className="px-2 py-2 text-start">
                    {t('contest.results.correct')}
                  </th>
                  <th scope="col" className="px-2 py-2 text-start">
                    {t('contest.results.time')}
                  </th>
                  <th scope="col" className="px-2 py-2 text-start">
                    {t('contest.results.prize')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((row) => (
                  <tr key={row.rank} className="border-b border-border last:border-0">
                    <td className="px-2 py-2 font-medium text-fg">{row.rank}</td>
                    <td className="px-2 py-2 text-fg">
                      {row.displayName ?? t('common.notAvailable')}
                    </td>
                    <td className="px-2 py-2 text-fg">{row.score}</td>
                    <td className="px-2 py-2 text-muted">
                      {row.correctCount}/{row.answeredCount}
                    </td>
                    <td className="px-2 py-2 text-muted">{formatDuration(row.totalTimeMs)}</td>
                    <td className="px-2 py-2 text-fg">
                      {row.isWinner ? `${row.prize} ${contest.asset}` : t('common.none')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {questions.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('contest.results.review')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            {questions.map((question) => (
              <div key={question.id} className="flex flex-col gap-2">
                <span className="text-xs uppercase tracking-wide text-muted">
                  {t(KIND_KEY[question.kind])} · {question.points}
                </span>
                <p className="text-sm font-medium text-fg">{question.prompt}</p>
                <ul className="flex flex-col gap-1">
                  {question.options.map((option) => {
                    const isCorrect = question.correctOptionIds.includes(option.id);
                    return (
                      <li
                        key={option.id}
                        className={
                          isCorrect
                            ? 'rounded-md bg-success-soft px-2 py-1 text-sm text-success-soft-fg'
                            : 'px-2 py-1 text-sm text-muted'
                        }
                      >
                        {option.label}
                        {isCorrect && (
                          <span className="ms-2 text-xs font-medium">
                            {t('contest.results.correctAnswer')}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {question.kind === 'numeric' && question.numericAnswer && (
                  <p className="text-sm text-muted">
                    {t('contest.results.correctAnswer')}: {question.numericAnswer}
                  </p>
                )}
                {question.explanation && (
                  <p className="text-sm text-muted">
                    {t('contest.results.explanation')}: {question.explanation}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <p className="mt-6 text-xs text-muted">{formatDateTime(contest.finalizedAt, locale)}</p>
    </div>
  );
}
